import {
  ORDER_RECEIVED,
  SELECTED_ORDER_CONFIRMED,
  SELECTED_ORDER_RECEIVED,
} from "../../constants/orderStatus";
import {
  CHANGE_FIELD_LABEL,
  getStaleDateFields,
  isAdvancingTransition,
  isAutoRecordedDateField,
  ORDER_DATE_FIELDS,
  STATUS_DATE_FIELD,
  toDateOrNull,
} from "./orderDateUpdate";
import { formatOrderDate } from "../utils/formatting";

// ステータス変更ダイアログ(一括 / 単票)と日付編集ダイアログに出す説明文を
// 組み立てる純関数。
//
// かつては同じ意味の文言を ConfirmStatusChangeModal と
// ConfirmSingleStatusChangeModal がそれぞれ組み立てており、
// 片方だけ直して一括と単票で案内が食い違う事故が起きやすかった。
// 「今回どの日付がどう書き換わるか」は業務ルールそのものなので、
// JSXではなくここに集約してJestで固める。

export const VARIANT_BULK = "bulk";
export const VARIANT_SINGLE = "single";

const RECEIVED_DATE_FIELD = STATUS_DATE_FIELD[ORDER_RECEIVED];
const RECEIVED_DATE_LABEL = CHANGE_FIELD_LABEL[RECEIVED_DATE_FIELD];

// 給与控除の対象は「検収済」のステータスで決まる(検収日の値では決まらない)。
// 工程を戻したあとに残る検収日を見て担当者が不安にならないよう、必ず添える。
const DEDUCTION_SCOPE_HEAD = `給与控除の対象は「${ORDER_RECEIVED}」のデータのみのため、`;
// 検収日を上書きすると控除の対象月が動く。担当者側では検知できないので明示する。
const RE_RECEIVE_NOTE = `あらためて「${SELECTED_ORDER_RECEIVED}」した場合、${RECEIVED_DATE_LABEL}はその実行日で上書きされます（既に控除済みの場合は対象月が変わるためご注意ください）。`;
const DEDUCTION_MONTH_NOTE = "給与控除の対象月が変わるため、既に控除済みの場合はご注意ください。";

const uniqueLabels = (fields) => [...new Set(fields.map(({ label }) => label))];

const joinSentences = (sentences) => sentences.filter(Boolean).join("");

// 工程を戻す・取消するときに「残る日付」の案内をまとめて1つ返す。
//
// 3つの話をこの順で並べる(該当するものだけ):
//   1. クリアできる後工程の日付(発注日・取消日)  … 消し方を案内する
//   2. 検収済から外れて残る検収日                … 消せないことと控除対象外を伝える
//   3. あらためて検収したときの上書き            … 控除の対象月が動く
//
// 1と2を1つの文脈にまとめているのは、赤い警告ブロックが縦に積み上がって
// 読まれなくなるのを避けるため。2と3は取消(検収済→取消済)でも必要になるが、
// getStaleDateFields は取消を「残る日付なし」と扱うので、ここで別途拾う。
export const buildRemainingDatesNotice = ({ orders, targetStatus, variant }) => {
  const list = (orders || []).filter(Boolean);
  const isSingle = variant === VARIANT_SINGLE;

  // 1. クリアできる後工程の日付
  const clearableByOrder = list.map((order) =>
    getStaleDateFields(order, targetStatus).filter(
      ({ autoRecorded }) => !autoRecorded
    )
  );
  const clearableCount = clearableByOrder.filter((fields) => fields.length > 0)
    .length;
  const clearableLabels = uniqueLabels(clearableByOrder.flat());

  // 2/3. 検収済から外れる注文
  const leaving =
    targetStatus === ORDER_RECEIVED
      ? []
      : list.filter((order) => order?.status === ORDER_RECEIVED);
  const leavingWithDate = leaving.filter((order) =>
    toDateOrNull(order?.[RECEIVED_DATE_FIELD])
  );

  return (
    joinSentences([
      clearableCount > 0
        ? isSingle
          ? `${clearableLabels.join(
              "・"
            )}はそのまま残ります。不要な場合は「日付編集」からクリアしてください。`
          : `${clearableCount} 件に後工程の日付（${clearableLabels.join(
              "・"
            )}）が残ります。不要な場合は「日付編集」からクリアしてください。`
        : null,
      leavingWithDate.length > 0
        ? isSingle
          ? `${RECEIVED_DATE_LABEL}は記録として残り、変更・削除はできません。${DEDUCTION_SCOPE_HEAD}この注文は控除されません。`
          : `${ORDER_RECEIVED}から外す ${leavingWithDate.length} 件の${RECEIVED_DATE_LABEL}は記録として残り、変更・削除はできません。${DEDUCTION_SCOPE_HEAD}これらは控除されません。`
        : null,
      leaving.length > 0 ? RE_RECEIVE_NOTE : null,
    ]) || null
  );
};

// 自動記録の日付(検収日)の読み取り専用の注記。
// 入力欄が無い理由と、実際に何が記録されるかを実行前に見せる。
const buildAutoRecordedNotice = ({
  dateLabel,
  count,
  now,
  existingDate,
  withExistingCount,
  untouchedLabels,
  isSingle,
}) => {
  const lines = [
    `${dateLabel}には実行日（${formatOrderDate(now)}）が記録されます。`,
  ];
  if (!isSingle && count > 0) {
    lines.push(`選択した ${count} 件すべてに同じ日付が入ります。`);
  }
  lines.push(`※ ${dateLabel}は給与控除の締めに使うため、遡って登録できません。`);
  if (withExistingCount > 0) {
    lines.push(
      isSingle
        ? `※ 現在の${dateLabel} ${formatOrderDate(
            existingDate
          )} は実行日で上書きされます。${DEDUCTION_MONTH_NOTE}`
        : `※ 既に${dateLabel}が入っている ${withExistingCount} 件も実行日で上書きされます。${DEDUCTION_MONTH_NOTE}`
    );
  }
  lines.push(`※ ${untouchedLabels}は変更されません。`);
  return lines.join("\n");
};

// 一括のときの「今回どう書き込まれるか」の一文。
// 前進(注文→発注 等)・取消は「今起きた出来事」なので、チェックOFFでも実行日時で
// 上書きされる。混在する選択では件数まで言う。
const buildBulkHintHead = ({
  dateLabel,
  count,
  isEnabled,
  isAllAdvancing,
  hasAdvancing,
  advancingCount,
}) => {
  if (isEnabled) {
    const timeNote = isAllAdvancing
      ? "時刻は実行した時刻になります。"
      : hasAdvancing
      ? "時刻は既存の値を維持します（未設定の注文と工程を進める注文は実行時刻）。"
      : "時刻は既存の値を維持します（未設定の場合は実行時刻）。";
    return `選択した${count}件すべてにこの日付が記録されます。${timeNote}`;
  }
  if (isAllAdvancing) {
    return `チェックしなくても各注文の${dateLabel}には実行日時が入ります（工程を進める操作のため）。`;
  }
  if (hasAdvancing) {
    return `チェックしなければ各注文の${dateLabel}は現在の値のままです（未設定の注文と、工程を進める ${advancingCount} 件には実行日時が入ります）。`;
  }
  return `チェックしなければ各注文の${dateLabel}は現在の値のままです（未設定の注文には実行日時が入ります）。`;
};

// 手動で指定できる日付(注文日・発注日・取消日)の補足。
// チェックON/OFFで意味が変わるので、確定前に結果を言い切る。
//
// ★ここは orderDateUpdate.js の resolveNextDateValue の写しでしかない。
//   前進・取消では「今起きた出来事」として実行日時で上書きするため、
//   チェックOFFでも既存値は維持されない。かつては advancing を見ずに一律
//   「現在の値のまま」「時刻は現在の値を維持します」と書いていたため、
//   工程を戻して日付が残った注文を進め直すときに説明と結果が食い違っていた。
const buildManualHint = ({
  dateLabel,
  count,
  dateInput,
  existingDate,
  withExistingCount,
  untouchedLabels,
  advancingCount,
  isSingle,
}) => {
  const isEnabled = !!dateInput?.enabled;
  const hasAdvancing = advancingCount > 0;
  const isAllAdvancing = count > 0 && advancingCount === count;

  if (isSingle) {
    if (isEnabled) {
      // 前進・取消では時刻の起点も実行時刻になる(前回の残骸の時刻を引き継がない)
      return `時刻は${
        existingDate && !hasAdvancing
          ? "現在の値を維持します"
          : "実行した時刻になります"
      }。`;
    }
    if (hasAdvancing) {
      return existingDate
        ? `チェックしなくても${dateLabel}には実行日時が入ります（現在の ${formatOrderDate(
            existingDate
          )} は上書きされます）。`
        : `チェックしなくても${dateLabel}には実行日時が入ります。`;
    }
    return existingDate
      ? `チェックしなければ${dateLabel}は ${formatOrderDate(
          existingDate
        )} のままです。`
      : `チェックしなければ${dateLabel}には実行日時が入ります。`;
  }

  const head = buildBulkHintHead({
    dateLabel,
    count,
    isEnabled,
    isAllAdvancing,
    hasAdvancing,
    advancingCount,
  });
  // 既に日付が入っている注文が何件あるかを見せる。
  // 前進が混ざるだけの場合は head が件数まで説明しているので足さない。
  let existingNote = "";
  if (withExistingCount > 0) {
    if (isEnabled || isAllAdvancing) {
      existingNote = `\n※ 既に${dateLabel}が入っている ${withExistingCount} 件も上書きされます。`;
    } else if (!hasAdvancing) {
      existingNote = `\n※ 既に${dateLabel}が入っている ${withExistingCount} 件はそのまま維持されます。`;
    }
  }
  return `${head}${existingNote}\n※ ${untouchedLabels}は変更されません。`;
};

// ステータス変更ダイアログの表示内容をまとめて返す。
//   orders  … 対象の注文doc配列(単票は1件だけ入れる)
//   variant … VARIANT_BULK | VARIANT_SINGLE
export const buildStatusChangeNotices = ({
  orders,
  targetStatus,
  dateInput,
  now,
  variant = VARIANT_BULK,
}) => {
  const list = (orders || []).filter(Boolean);
  const dateField = STATUS_DATE_FIELD[targetStatus] ?? null;
  const dateLabel = dateField ? CHANGE_FIELD_LABEL[dateField] : null;
  const isAutoRecorded = isAutoRecordedDateField(dateField);
  const isSingle = variant === VARIANT_SINGLE;
  const untouchedLabels = ORDER_DATE_FIELDS.filter(
    ({ field }) => field !== dateField
  )
    .map(({ label }) => label)
    .join("・");
  const withExistingCount = dateField
    ? list.filter((order) => toDateOrNull(order?.[dateField])).length
    : 0;
  const existingDate = dateField ? toDateOrNull(list[0]?.[dateField]) : null;
  // 前進・取消の件数。「チェックしなければ現在の値のまま」が成り立つのは後退・復帰だけなので、
  // 説明文の出し分けに使う(orderDateUpdate.js の resolveNextDateValue と揃える)。
  const advancingCount = list.filter((order) =>
    isAdvancingTransition(order?.status, targetStatus)
  ).length;

  const shared = {
    dateLabel,
    count: list.length,
    existingDate,
    withExistingCount,
    untouchedLabels,
    advancingCount,
    isSingle,
  };

  return {
    dateField,
    dateLabel,
    isAutoRecorded,
    autoNotice:
      dateField && isAutoRecorded
        ? buildAutoRecordedNotice({ ...shared, now })
        : null,
    hint:
      dateField && !isAutoRecorded
        ? buildManualHint({ ...shared, dateInput })
        : null,
    remainingDatesNotice: buildRemainingDatesNotice({
      orders: list,
      targetStatus,
      variant,
    }),
  };
};

// 日付編集ダイアログで自動記録の日付(検収日)の行に添える説明。
// ここは「変更したい人が最初に来る場所」なので、変更できない理由と
// その注文で実際に取れる手段だけを書く。
//
// 注文の状態で言うべきことが変わる:
//   検収済         … 直す手段(戻して再検収)がある
//   それ以外＋値あり … 工程を戻した結果の記録。消せないが控除もされない
//   値なし         … 説明だけで十分(余計な手順を読ませない)
export const buildAutoRecordedEditNote = ({
  label = RECEIVED_DATE_LABEL,
  status,
  value,
} = {}) => {
  const head = `${label}は「${SELECTED_ORDER_RECEIVED}」を実行した日として自動で記録されます。`;
  if (status === ORDER_RECEIVED) {
    return `${head}ここでは変更・削除できません。直す場合は「${SELECTED_ORDER_CONFIRMED}」に戻してから、あらためて「${SELECTED_ORDER_RECEIVED}」してください（${label}はその実行日になります）。`;
  }
  if (toDateOrNull(value)) {
    return `${head}この注文は「${ORDER_RECEIVED}」ではないため、これは過去に検収したときの記録です（変更・削除はできません）。${DEDUCTION_SCOPE_HEAD}この注文は控除されません。`;
  }
  return head;
};
